import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Wrench,
    Zap,
    Droplets,
    Paintbrush,
    TreeDeciduous,
    Shield,
    Home,
    ChevronRight,
    Loader2,
    Info,
    Plus
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useProviderCategories } from "@/hooks/useProviders";
import { Button } from "@/components/ui/button";
import AddProviderDialog from "@/components/AddProviderDialog";
import { useQueryClient } from "@tanstack/react-query";

const categoryIcons = {
    "plumbing": Droplets,
    "electrical": Zap,
    "painting": Paintbrush,
    "garden-pool": TreeDeciduous,
    "security": Shield,
    "roofing": Home,
    "hvac": Wrench,
    "gas": Zap,
};

const getIconForCategory = (category) => {
    const lowerCategory = category.toLowerCase();
    for (const [key, icon] of Object.entries(categoryIcons)) {
        if (lowerCategory.includes(key)) {
            return icon;
        }
    }
    return Wrench;
};

const Directory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { categories, loading } = useProviderCategories();
    const queryClient = useQueryClient();
    const [isAddProviderOpen, setIsAddProviderOpen] = useState(false);

    // Auto-filter based on navigation state
    useEffect(() => {
        const state = location.state;
        if (state?.filterCategory) {
            navigate("/service-providers", {
                state: {
                    taskTitle: state.displayCategory || state.filterCategory,
                    category: state.filterCategory
                },
                replace: true
            });
        }
    }, [location.state, navigate]);

    const handleCategoryClick = (category) => {
        navigate("/service-providers", {
            state: { taskTitle: category.name, category: category.id }
        });
    };

    // Default categories if none exist in DB yet
    const defaultCategories = [
        { id: "plumbing", name: "Plumbing", count: 0 },
        { id: "electrical", name: "Electrical", count: 0 },
        { id: "painting", name: "Painting", count: 0 },
        { id: "garden-pool", name: "Garden & Pool", count: 0 },
        { id: "security", name: "Security", count: 0 },
        { id: "roofing", name: "Roofing", count: 0 },
        { id: "hvac", name: "HVAC", count: 0 },
        { id: "gas", name: "Gas", count: 0 },
    ];

    // Merge database counts into the default list
    const displayCategories = defaultCategories.map(defCat => {
        const dbCat = categories.find(c =>
            c.name.toLowerCase() === defCat.name.toLowerCase() ||
            c.id === defCat.id
        );
        return dbCat ? { ...defCat, id: dbCat.id, count: dbCat.count } : defCat;
    });

    // Also include any categories from DB that AREN'T in the default list
    const extraCategories = categories.filter(dbCat =>
        !defaultCategories.some(defCat =>
            defCat.name.toLowerCase() === dbCat.name.toLowerCase() ||
            defCat.id === dbCat.id
        )
    );

    const allCategories = [...displayCategories, ...extraCategories];

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] pb-24">
            <PageHeader pageName="Directory" />

            {/* Main Content */}
            <main className="px-6" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 7rem)" }}>
                <div className="max-w-lg mx-auto space-y-4">
                    <Alert className="bg-primary/5 border-primary/20 mb-6">
                        <Info className="h-4 w-4 text-primary" />
                        <AlertTitle className="text-primary font-semibold">Growing Community</AlertTitle>
                        <AlertDescription className="text-muted-foreground text-xs mt-1">
                            This directory will populate as more providers get verified.
                            Don't worry—you can always see providers you manually add,
                            but we hide others until they are fully verified for safety.
                        </AlertDescription>
                    </Alert>

                    <div className="flex justify-between items-center mb-6">
                        <p className="text-sm font-semibold text-muted-foreground">
                            Browse by category
                        </p>
                        <Button
                            onClick={() => setIsAddProviderOpen(true)}
                            size="sm"
                            className="rounded-full px-4 gap-1.5 font-bold shadow-md shadow-primary/10"
                        >
                            <Plus className="w-4 h-4" /> Add Provider
                        </Button>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                    ) : (
                        allCategories.map((category) => {
                            const Icon = getIconForCategory(category.name);
                            return (
                                <GlassCard key={category.id} className="p-4">
                                    <button
                                        onClick={() => handleCategoryClick(category)}
                                        className="w-full flex items-center gap-4"
                                    >
                                        <div className="p-3 rounded-xl bg-primary/10 shrink-0">
                                            <Icon className="w-5 h-5 text-primary" />
                                        </div>
                                        <div className="flex-1 text-left">
                                            <h3 className="font-bold text-sm text-foreground">{category.name}</h3>
                                            <p className="text-xs text-muted-foreground">
                                                {category.count > 0
                                                    ? `${category.count} provider${category.count > 1 ? 's' : ''} available`
                                                    : "No providers yet"
                                                }
                                            </p>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                                    </button>
                                </GlassCard>
                            );
                        })
                    )}
                </div>
            </main>
            <AddProviderDialog
                open={isAddProviderOpen}
                onOpenChange={setIsAddProviderOpen}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ["provider_categories"] });
                    queryClient.invalidateQueries({ queryKey: ["providers"] });
                }}
            />
        </div>
    );
};

export default Directory;
